<template>
  <Polyline :options="polylineOptions" ref="polylineRef"
            @click="onPolylineClick"
            @mouseover="onPolylineMouseOver"
            @mouseout="onPolylineMouseOut">
  </Polyline>
  <Polyline :options="hitPolylineOptions"
            @click="onPolylineClick"
            @mouseover="onPolylineMouseOver"
            @mouseout="onPolylineMouseOut">
  </Polyline>
  <AlarmMarker v-for="data in alarmPositions" v-bind:key="data.position.id" ref="alarmMarkersRef"
               :data="data.infoWindowOptions" :position-data="data.position" />
  <StopMarker v-if="showStays && stay" ref="stopMarkerRef"
              :stopData="stayData" :info-window-header="infoWindowHeaderContent"/>
  <StopMarker v-for="(data, index) in showStops ? (stops ?? []) : []" v-bind:key="index" ref="temporaryStopMarkersRef"
              :stopData="data" :info-window-header="infoWindowHeaderContent" :color-variant="formatStopColorVariant(data)" />
  <FuelCapOpenMarker v-for="data in trip?.fuelCapStatus ?? []"
                     v-bind:key="data.timestamp" :info-window-header="infoWindowHeaderContent" :data="data" />
  <RefuelMarker v-if="trip?.reFuel?.totalRefuelLiters"
                :data="trip?.reFuel" :info-window-header="infoWindowHeaderContent" ref="refuelMarkerRef" />
  <InfoWindow ref="infoWindowRef" :options="infoWindowOptions" v-if="isSelected">
    <EcoEventsInfo :data="infoWindowOptions" />
  </InfoWindow>
  <Marker v-if="isSelected" :options="cursorIconOptions" ref="cursorIconRef" @click="openInfoWindow"/>
  <InfoWindow v-if="!isSelected && indexInfoOptions" ref="indexInfoRef" :options="indexInfoOptions"/>
</template>

<script>
import {InfoWindow, Polyline, Marker} from "vue3-google-map";
import {entityDataManager, GoogleMapsManager} from "@/assets/js/data-manipulation";
import EcoEventsInfo from "@/components/pages/EcoEventsInfo.vue";
import AlarmMarker from "@/components/pages/partials/maps/AlarmMarker.vue";
import StopMarker from "@/components/pages/partials/maps/StopMarker.vue";
import FuelCapOpenMarker from "@/components/pages/partials/maps/FuelCapOpenMarker.vue";
import RefuelMarker from "@/components/pages/partials/maps/RefuelMarker.vue";
import {mapState} from "vuex";

const ROUTE_Z_INDEX = 1000;
const SELECTED_ROUTE_Z_INDEX = 1100;
const ROUTE_HIT_TARGET_STROKE_WEIGHT = 18;
const ROUTE_HIT_TARGET_OPACITY = 0.01;

export default {
  name: "MapRoute",
  components: {RefuelMarker, FuelCapOpenMarker, StopMarker, AlarmMarker, EcoEventsInfo, Polyline, InfoWindow, Marker},
  props: {
    positions: {
      type: Array,
      required: true
    },
    stay: {
      type: [Object, null],
      required: true
    },
    stops: {
      type: [Array, null],
    },
    trip: {
      type: [Object, null],
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    showAlarms: {
      type: Boolean,
      default: Boolean(+process.env.VUE_APP_TRIPS_SHOW_ALARMS_DEFAULT)
    },
    showStays: {
      type: Boolean,
      default: Boolean(+process.env.VUE_APP_TRIPS_SHOW_STAY_DEFAULT)
    },
    showStops: {
      type: Boolean,
      default: Boolean(+process.env.VUE_APP_TRIPS_SHOW_STOPS_DEFAULT)
    },
    customOptions: Object
  },
  emits: ["selected", "mouseMove"],
  data() {
    return {
      selectedPosition: null,
      infoWindowOptions: null,
      cursorIconOptions: null,
      indexInfoOptions: null,
      customMarkerShown: null,
      lineColor: '#1a2ad3',
      positionLastValidValues: {},
      infoWindowHeaderContent: null,
    }
  },
  computed: {
    ...mapState(['drivers', 'vehicles']),
    routeZIndex() {
      return this.isSelected ? SELECTED_ROUTE_Z_INDEX : ROUTE_Z_INDEX;
    },
    polylineOptions() {
      return {
        ...{
          path: this.polylinePathPositions,
          geodesic: true,
          strokeColor: this.lineColor,
          strokeOpacity: 0.8,
          strokeWeight: this.isSelected ? 13 : 5,
          zIndex: this.routeZIndex
        },
        ...this.customOptions
      };
    },
    hitPolylineOptions() {
      return {
        path: this.polylineOptions.path,
        geodesic: this.polylineOptions.geodesic,
        clickable: true,
        strokeColor: this.polylineOptions.strokeColor,
        strokeOpacity: ROUTE_HIT_TARGET_OPACITY,
        strokeWeight: ROUTE_HIT_TARGET_STROKE_WEIGHT,
        zIndex: Number(this.polylineOptions.zIndex ?? this.routeZIndex) + 1
      };
    },
    polylinePathPositions() {
      return this.positions.map(position => entityDataManager.getGoogleGeoByPosition(position));
    },
    stayData() {
      const stay = this.stay;
      if (stay) {
        stay.index = this.index;
      }

      return stay;
    },
    alarmPositions() {
      if (!this.showAlarms) {
        return [];
      }

      return this.positions
          .filter((position) => entityDataManager.isEcoEventSupportedAlarm(position['alarm']))
          .map((position) => {
            return {
              position: position,
              infoWindowOptions: this.computeInfoWindowOptions(position)
            }
          });
    },
    isSelected() {
      return this.selectedPosition !== null;
    }
  },
  beforeMount() {
    this.infoWindowHeaderContent = this.createInfoWindowHeaderContent();
  },
  methods: {
    onPolylineClick(data) {
      const coordinates = GoogleMapsManager.getCoordinatesFromGoogleData(data.latLng);
      this.selectPosition(coordinates);
      this.computeCursorInfoWindowOptions();
      this.computeCursorIconOptions();
      this.setCursorPosition(coordinates);
      this.openInfoWindow();
      this.$emit('selected', this);
    },
    onPolylineMouseOver(data) {
      const coordinates = GoogleMapsManager.getCoordinatesFromGoogleData(data.latLng);
      this.setIndexInfoOptions(coordinates);
    },
    onPolylineMouseOut() {
      this.resetIndexInfoOptions();
    },
    selectPosition(coordinates) {
      this.selectedPosition = this.getNearestPosition(coordinates);
    },
    selectPositionById(id) {
      this.selectedPosition = this.positions.find(x => x.id === id);
      this.computeCursorInfoWindowOptions();
      this.computeCursorIconOptions();
      this.setCursorPosition(entityDataManager.getGoogleGeoByPosition(this.selectedPosition));
    },
    deselectPosition() {
      if (this.selectedPosition === null) {
        return;
      }

      this.closeAllInfoWindows();
      this.selectedPosition = null;
      this.infoWindowOptions = null;
      this.cursorIconOptions = null;
    },
    computeCursorInfoWindowOptions() {
      this.infoWindowOptions = this.computeInfoWindowOptions(this.selectedPosition);
      this.infoWindowOptions.pixelOffset = {width: 0, height: -15};
    },
    computeInfoWindowOptions(position) {
      const time = entityDataManager.getTimeByPosition(position);

      const result = {
        headerContent: this.infoWindowHeaderContent,
        address: entityDataManager.getAddressByReportStop(position),
        speed: entityDataManager.getSpeedInKmphByPosition(position),
        time: time !== null ? time.format(this.$t('settings.generic.date-time-format-moment-extended')) : '',
        driver: this.trip?.driver,
        fuel: position?.fuel ?? this.positionLastValidValues?.fuel,
        power: position?.power ?? this.positionLastValidValues?.power,
        rpm: position?.rpm ?? this.positionLastValidValues?.rpm,
        throttle: position?.throttle ?? this.positionLastValidValues?.throttle,
        temperature: position?.temperature,
        humidity: position?.humidity
      };

      this.positionLastValidValues = {
        fuel: result.fuel,
        power: result.power,
        rpm: result.rpm,
        throttle: result.throttle,
      };

      return result;
    },
    createInfoWindowHeaderContent() {
      const header = document.createElement("div");
      header.className = "map-info-window-header";

      const vehicle = entityDataManager.getVehicleByDeviceId(this.vehicles, this.trip?.deviceId);
      const vehicleName = vehicle?.nickname ?? '';
      header.textContent = vehicleName
          ? `${vehicleName}\n${this.$t('reports.trips.eco-events.index')} ${this.index}`
          : `${this.$t('reports.trips.eco-events.index')} ${this.index}`;
      header.style.whiteSpace = 'pre-line';

      return header;
    },
    computeCursorIconOptions() {
      if (!this.cursorIconOptions) {
        this.cursorIconOptions = {};
      }

      this.cursorIconOptions = {
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 5,
        },
      };
    },
    setCursorPosition(coordinates) {
      this.infoWindowOptions.position = coordinates;
      this.$refs.infoWindowRef?.infoWindow?.setPosition(this.infoWindowOptions.position);
      this.cursorIconOptions.position = coordinates;
      this.$refs.cursorIconRef?.marker?.setPosition(this.cursorIconOptions.position);
    },
    setIndexInfoOptions(coordinates) {
      if (this.isSelected) {
        this.indexInfoOptions = null;
      }

      this.indexInfoOptions = {
        position: coordinates,
        content: `${this.$t('reports.trips.eco-events.index')} ${this.index}`,
        headerDisabled: true,
        pixelOffset: {width: 0, height: -5}
      };
      this.$refs.indexInfoRef?.infoWindow?.setPosition(this.indexInfoOptions.position);
    },
    resetIndexInfoOptions() {
     this.indexInfoOptions = null;
    },
    initializeInfoWindow() {
      const customMarker = this.getAlarmMarker(this.selectedPosition) ??
                           this.getStopMarker(this.selectedPosition) ??
                           this.getRefuelMarker(this.selectedPosition);

      if (customMarker) {
        if (this.customMarkerShown !== customMarker) {
          this.closeAllInfoWindows();
          customMarker.showInfoWindow();
          this.customMarkerShown = customMarker;
        }
      }
      else {
        if (this.customMarkerShown) {
          this.closeAllInfoWindows();
          this.customMarkerShown = null;
          this.openInfoWindow();
        }
        this.computeCursorInfoWindowOptions();
      }
    },
    openInfoWindow() {
      this.$refs.infoWindowRef?.open();
    },
    closeInfoWindow() {
      this.$refs.infoWindowRef?.close();
    },
    closeAllInfoWindows() {
      this.closeInfoWindow();
      this.customMarkerShown?.closeInfoWindow();
    },
    isInfoWindowOpen() {
      return this.$refs.infoWindowRef?.infoWindow.isOpen || this.customMarkerShown?.isInfoWindowOpen();
    },
    formatStopColorVariant(stop) {
      const durationMs = stop?.duration ?? 0;
      const greenThresholdMs = Number(process.env.VUE_APP_TRIPS_GREEN_STOP_MIN_TIME_SECONDS ?? 0) * 1000;
      const yellowThresholdMs = Number(process.env.VUE_APP_TRIPS_YELLOW_STOP_MIN_TIME_SECONDS ?? 0) * 1000;
      const redThresholdMs = Number(process.env.VUE_APP_TRIPS_RED_STOP_MIN_TIME_SECONDS ?? 0) * 1000;

      if (durationMs >= redThresholdMs) {
        return 'red';
      }

      if (durationMs >= yellowThresholdMs && durationMs < redThresholdMs) {
        return 'yellow';
      }

      if (durationMs >= greenThresholdMs && durationMs < yellowThresholdMs) {
        return 'green';
      }

      return null;
    },
    getNearestPosition(pLatLng) {
      const polyline = this.getPolyline();
      if (!polyline) {
        return;
      }

      let minDiff = 9999999999;
      const path = polyline.getPath();
      const count = path.length || 0;

      let point = null;
      let dist = null;
      let index = null;

      for (let n = 0; n < count - 1; n++) {
        if (n === 0) {
          point = path.getAt(n);
          dist = window.google.maps.geometry.spherical.computeDistanceBetween(pLatLng, point);
        }

        const pointb = path.getAt(n + 1);
        const distb =  window.google.maps.geometry.spherical.computeDistanceBetween(pLatLng, pointb);
        const distp2p = window.google.maps.geometry.spherical.computeDistanceBetween(point, pointb);
        const pdiff = dist + distb - distp2p;

        if (pdiff < minDiff) {
          minDiff = pdiff.toFixed(8);
          index = n;
        }

        point = pointb;
        dist = distb;
      }

      if (index === (count - 2)) {
        const lastPoint = path.getAt(count - 1);
        const lastPointDist = window.google.maps.geometry.spherical.computeDistanceBetween(pLatLng, lastPoint);
        if (lastPointDist <= 1.5) {
          index = count - 1;
        }
      }

      return this.positions[index];
    },
    getPolyline() {
      return this.$refs?.polylineRef?.polyline;
    },
    getAlarmMarker(position) {
      return this.$refs.alarmMarkersRef?.find(x =>
          x.position.lat === position.latitude &&
          x.position.lng === position.longitude);
    },
    getStopMarker(position) {
      if (this.$refs.stopMarkerRef?.position.lat === position.latitude &&
          this.$refs.stopMarkerRef?.position.lng === position.longitude) {
        return this.$refs.stopMarkerRef;
      }

      const temporaryStopMarker = this.$refs.temporaryStopMarkersRef?.find(stop =>
          stop.position.lat === position.latitude &&
          stop?.position.lng === position.longitude);

      return temporaryStopMarker ?? null;
    },
    getRefuelMarker(position) {
      if (this.$refs.refuelMarkerRef?.position.lat === position.latitude &&
          this.$refs.refuelMarkerRef?.position.lng === position.longitude) {
        return this.$refs.refuelMarkerRef;
      }

      return null;
    }
  }
}
</script>

<style scoped lang="scss">

</style>
